<script setup lang="ts">
/**
 * /gov-accounts —— 监管端账号的开户与停用(v2 · O7 按画板重排,深色;S-45)。
 *
 * 人在这里要能做完整条链路(S-11 的运营端那一半,一字未变):
 *   ① 给海关/边检开一个账号(用户名 + 口令)
 *   ② 看到它出现在列表里(「启用」)
 *   ③ 点「停用」—— **下一次请求立刻生效**(不需要等他的令牌过期,
 *      监管端守卫每次都回库查状态;这是「令牌是一份缓存,缓存的有效期
 *      就是令牌的有效期」那条纪律的表现)
 *   ④ 再点「启用」把他放回来 —— 画板只画了「停用」那半边,
 *      而同一个开关的另一态是契约里真有的(PATCH {enabled:true}),补齐,不丢
 *
 * 页面刻意**不做改口令** —— M-07 的原文是「开户与停用」,契约里也只有这三条。
 * 改口令是一个新能力,不该悄悄混在停用里(停用页上出现一个口令输入框,
 * 点错一次就是给陌生人开了门)。
 *
 * 时间显示一律走 @sea-drone/shared 的 formatShanghaiDateTime(Y-03):
 * 库里存 UTC、给人看的是东八区 —— 在这里手写 toLocaleString 的话,
 * 容器 TZ=UTC 时页面上会少 8 小时,而且没有任何地方会提示。
 *
 * ═══ 与画板的差异(都记在 design-copy 的 O7 帧里)═══
 *
 * ① 画板的第三张统计卡「待激活」**不画**:契约里监管端账号只有 enabled 布尔,
 *    开户就是「用户名 + 口令当场设定」,没有激活步骤 —— 没有任何值能渲染成它。
 * ② 画板的「重发邀请」动作与整套「邀请 / 激活」流**不建**:契约只有
 *    GET/POST /api/admin/gov-accounts 与 PATCH {enabled} 三条,没有任何邀请/重发接口。
 * ③ 「所属单位」「范围」「最近登录」三列在 GovAccount 里**没有字段** → 画破折号,
 *    各登记一条 @unimplemented 标记(见下);不拿别处的名字或时间顶替。
 * ④ 状态标签的词形按画板取两字的「启用」(换版前是「启用中」);停用态的
 *    「已停用」与操作列的「启用」是同一个开关的另一半,画板没画。
 * ⑤ 「开户时间」那一列画板没画:它是换版前就在表上的信息,而且没有第二个落点
 *    (契约里 GovAccount.createdAt 只有这一处显示)→ 保留,排在画板那六列之后。
 * ⑥ 换版前表上还有一列「类别」,这一版**裁掉**:列表接口只回 kind=gov 的账号,
 *    那一列每一行都是同一个词,与页头标题重复(契约形状见 opsListGovAccounts)。
 */
import { computed, h, onMounted, ref } from 'vue'
import { NButton, NForm, NFormItem, NInput, NModal, NPopover, NSpace, useMessage, type DataTableColumns } from 'naive-ui'
import {
  DataTable,
  ErrorBanner,
  NavIcon,
  PageHeader,
  PanelCard,
  StatTile,
  StatusTag,
  type ScopeNote,
} from '@sea-drone/ui'
import { formatShanghaiDateTime } from '@sea-drone/shared'
import { api, ApiError, type GovAccount } from '../api/client'

/** 这一页是运营端的深色页,底座由 S-41 的第三档 tone 给(AppShell 上已挂同一档) */
const TONE = 'ops-dark' as const

const message = useMessage()

const accounts = ref<GovAccount[]>([])
const username = ref('')
const password = ref('')
const busy = ref(false)
const lastError = ref<string | null>(null)
/** 返回体里的 total —— 服务端计数,不是本页行数 */
const accountTotal = ref(0)
const createOpen = ref(false)
/** 哪一行的「停用」确认浮层开着 */
const confirmId = ref<string | null>(null)

const NOTES: readonly ScopeNote[] = [
  {
    title: '这一页能做什么',
    body:
      '给海关 / 边检开一个账号（用户名 + 口令）→ 看到它出现在列表里（启用）→ 点「停用」，' +
      '下一次请求立刻生效（不需要等他的令牌过期，监管端守卫每次都回库查状态）→ 再点「启用」把他放回来。' +
      '口令只在这一刻进系统，库里存的是 scrypt 散列（与运营账号同一套）。',
    refs: ['M-07'],
  },
  {
    title: '两张统计卡的数字从哪来',
    body:
      '「在册账号」是服务端计数（gov-accounts 列表的 total，不止本页）。「本月登录」契约里' +
      '**没有数据源**：返回体里唯一的字段是开户时间（createdAt），没有登录时间，也没有任何' +
      '会话 / 登录记录接口 —— 所以画「—」并登记一条 @unimplemented。不许数本页条数，' +
      '也不许拿开户时间顶替登录时间（那是一句看不出来的假话）。',
    refs: ['S-45'],
  },
  {
    title: '画板上那三处「邀请 / 激活」语汇为什么一个都没有',
    body:
      '画板预设了一个「发出邀请、等对方激活」的状态机：第三张统计卡「待激活」、状态标签里的' +
      '「待激活」、操作里的「重发邀请」。而契约里开户就是**用户名 + 口令当场设定**' +
      '（GovAccount 只有 enabled 布尔），没有任何邀请 / 重发接口 —— 没有值能渲染成「待激活」，' +
      '「重发邀请」是一颗没有接口可调的按钮。整条流不建，三处登记在 design-copy 的 O7 帧豁免里。' +
      '口令之外没有第二条凭据投递通道：开户之后把账号与口令当面交给对方。',
    refs: ['M-07'],
  },
  {
    title: '「所属单位」「范围」「最近登录」三列为什么是破折号',
    body:
      '契约的 GovAccount 只有 accountId / username / kind / enabled / createdAt —— 「舟山海关 · 定海办」' +
      '这种单位名与「商船监管 / 全量只读」这种范围，返回体里都不存在（范围 / 权限在鉴权层是' +
      '**整端只读**，不是每账号可配），登录时间也没有任何会话记录的出口。三列按「拿不到就画破折号」' +
      '处理，各登记一条 @unimplemented；不拿别处的名字顶替。',
    refs: ['M-07', 'L0-01'],
  },
  {
    title: '为什么没有改口令',
    body:
      '页面刻意不做改口令 —— M-07 的原文是「开户与停用」，契约里也只有这三条。' +
      '改口令是一个新能力，不该悄悄混在停用里（停用页上出现一个口令输入框，点错一次就是给陌生人开了门）。',
    refs: ['M-07'],
  },
]

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function refresh(): Promise<void> {
  try {
    const page = await api.listGovAccounts()
    accounts.value = page.items
    accountTotal.value = page.total
    lastError.value = null
  } catch (e) {
    show(e)
  }
}

async function create(): Promise<void> {
  if (username.value.trim() === '' || password.value === '') {
    message.warning('用户名和口令都要填')
    return
  }
  busy.value = true
  try {
    const created = await api.createGovAccount({ username: username.value.trim(), password: password.value })
    message.success(`已开监管端账号「${created.username}」—— 把账号和口令当面交给海关/边检的人`)
    username.value = ''
    password.value = ''
    // 开完就收起弹窗:口令已经交出去了,留着一个空表单只会让人以为自己没点成功
    createOpen.value = false
    await refresh()
  } catch (e) {
    show(e)
  } finally {
    busy.value = false
  }
}

async function setEnabled(a: GovAccount, enabled: boolean): Promise<void> {
  try {
    const after = await api.patchGovAccount(a.accountId, { enabled })
    message.success(
      after.enabled
        ? `「${after.username}」已启用`
        : `「${after.username}」已停用 —— 他手上的令牌下一次请求就失效（不等 12 小时过期）`,
    )
    await refresh()
  } catch (e) {
    show(e)
  }
}

/** 停用是一次点错就要去喊人恢复的操作,所以先收一次确认;确认之后调用的仍是同一个 setEnabled */
function confirmDisable(a: GovAccount): void {
  confirmId.value = null
  void setEnabled(a, false)
}

// ── 四处缺口(画「—」;契约里没有对应字段/口径)───────────────────
//
// @unimplemented(reason="「本月登录」要按登录时间数人头,而返回体里只有 createdAt(开户时间),没有任何登录时间、会话或登录流水接口。拿「本页条数」或开户时间顶替都会在页面上看不出来 —— 按「拿不到就画破折号」处理。要它先给 GovAccount 加最近登录时间(并决定登录流水怎么记)", owner="me", blocks="O7 统计卡「本月登录」")
//
// @unimplemented(reason="「所属单位」(舟山海关 · 定海办 这类)在 GovAccount 里没有对应字段 —— 账号只有 accountId / username / kind / enabled / createdAt。拿用户名猜单位是一句看起来完全正常的假话。按「拿不到就画破折号」处理。要它先给账号加所属单位字段(开户时登记)", owner="me", blocks="O7 表格「所属单位」列")
//
// @unimplemented(reason="「范围」(商船监管 / 全量只读)在契约里不存在:监管端的范围是**整端只读**(鉴权层的全局守卫,M-07 / L0-01),不是每账号可配的策略,没有任何字段承载它。按「拿不到就画破折号」处理。真要每账号配范围,那是一条新的权限模型决策,不是补一个字段", owner="me", blocks="O7 表格「范围」列")
//
// @unimplemented(reason="「最近登录」在 GovAccount 里没有对应字段(时间只有 createdAt = 开户时间,实现里那一列叫「开户时间」,画板把它换成了登录时间)。没有登录时间与会话记录的输出,按「拿不到就画破折号」处理;拿开户时间顶替是一句看不出来的假话(同上面统计卡那条)", owner="me", blocks="O7 表格「最近登录」列")

/**
 * 六列 + 开户时间(画板 O7 的表头逐字 + 一列保留的旧信息):
 *   账号 username(等宽)· 所属单位 **无字段** → 破折号 · 范围 **无字段** → 破折号 ·
 *   状态 enabled(两字词形的「启用」按画板;「已停用」是同一开关的另一态)·
 *   最近登录 **无字段** → 破折号 · 开户时间 createdAt(画板没画,保留的旧信息)·
 *   操作「停用」/「启用」(同一个 PATCH 的两态,画板只画了停用那半边)。
 * 三列无字段的理由各挂一条 @unimplemented 标记(见上);不拿别处的字段顶替。
 */
const columns = computed<DataTableColumns<GovAccount>>(() => [
  { title: '账号', key: 'username', width: 200, render: (row) => h('span', { class: 'account-name' }, row.username) },
  // 所属单位:契约里没有这个字段 —— 破折号,理由见上面那条 @unimplemented
  { title: '所属单位', key: 'org', width: 160, render: () => h('span', { class: 'dash' }, '—') },
  // 范围:契约里不存在(整端只读,不是每账号可配)—— 破折号,理由见上面那条 @unimplemented
  { title: '范围', key: 'scope', width: 120, render: () => h('span', { class: 'dash' }, '—') },
  {
    title: '状态',
    key: 'enabled',
    width: 110,
    // 状态标签与两端其它状态同一个 pill(StatusTag):绿色 = 还在这份名册上(启用),
    // 灰色 = 已停用。两字词形按画板;这一对词不是枚举标签表里的值(它是布尔),
    // 所以在这里就地给出,不走标签函数
    render: (row) =>
      h(StatusTag, {
        status: row.enabled ? 'enabled' : 'disabled',
        label: row.enabled ? '启用' : '已停用',
        group: row.enabled ? 'done' : 'draft',
        tone: TONE,
        size: 'sm',
      }),
  },
  // 最近登录:契约里没有这个字段 —— 破折号,理由见上面那条 @unimplemented
  { title: '最近登录', key: 'lastLoginAt', width: 120, render: () => h('span', { class: 'dash' }, '—') },
  // 开户时间:画板没画这一列,而它是换版前就在表上的信息、且没有第二个落点(见文件头 ⑤)
  {
    title: '开户时间',
    key: 'createdAt',
    width: 170,
    render: (row) => h('span', { class: 'mono-time' }, formatShanghaiDateTime(row.createdAt)),
  },
  {
    title: '操作',
    key: 'actions',
    width: 110,
    render: (row) =>
      row.enabled
        ? h(
            NPopover,
            {
              trigger: 'click',
              placement: 'left',
              show: confirmId.value === row.accountId,
              'onUpdate:show': (v: boolean) => (confirmId.value = v ? row.accountId : null),
            },
            {
              trigger: () => h(NButton, { size: 'tiny', type: 'error' }, { default: () => '停用' }),
              default: () =>
                h('div', { class: 'confirm' }, [
                  h('div', { class: 'confirm-text' }, `停用后他手上的令牌下一次请求就失效。确定停用「${row.username}」？`),
                  h(NSpace, { justify: 'end', size: 8 }, {
                    default: () => [
                      h(NButton, { size: 'tiny', onClick: () => (confirmId.value = null) }, { default: () => '取消' }),
                      h(NButton, { size: 'tiny', type: 'error', onClick: () => confirmDisable(row) }, { default: () => '确定停用' }),
                    ],
                  }),
                ]),
            },
          )
        : h(NButton, { size: 'tiny', type: 'primary', onClick: () => void setEnabled(row, true) }, { default: () => '启用' }),
  },
])

onMounted(() => {
  void refresh()
})
</script>

<template>
  <div class="page">
    <PageHeader :tone="TONE" title="监管端账号" :notes="NOTES">
      <template #actions>
        <!-- 画板页头右端就是这颗开户按钮(它是这一页的主按钮,语义品牌蓝) -->
        <n-button type="primary" @click="createOpen = true">
          <template #icon>
            <NavIcon name="userPlus" :size="16" />
          </template>
          开一个监管端账号
        </n-button>
      </template>
    </PageHeader>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="上一次操作失败" />

    <!--
      两张统计卡(画板三张里的前两张):「在册账号」= 服务端计数(真数);
      「本月登录」契约里没有数据源,画「—」+ 一条 @unimplemented。
      画板第三张「待激活」不画(状态不存在,登记在 design-copy 的 O7 帧豁免里)。
    -->
    <div class="stats">
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="在册账号" :value="accountTotal" tone="brand" />
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="本月登录" value="—" tone="brand" />
    </div>

    <PanelCard :tone="TONE" padding="md">
      <DataTable
        :tone="TONE"
        :columns="columns"
        :data="accounts"
        :row-key="(row: GovAccount) => row.accountId"
        empty-text="还没有监管端账号 —— 点右上角「开一个监管端账号」"
      />
    </PanelCard>

    <n-modal v-model:show="createOpen" preset="card" title="给海关 / 边检开一个账号">
      <n-form @submit.prevent="create">
        <n-form-item label="用户名">
          <n-input v-model:value="username" placeholder="用户名" />
        </n-form-item>
        <n-form-item label="口令">
          <n-input
            v-model:value="password"
            type="password"
            show-password-on="click"
            placeholder="口令（至少 8 位）"
            @keyup.enter="create"
          />
        </n-form-item>
      </n-form>
      <p class="modal-note">
        口令只在这一刻进系统，库里存的是 scrypt 散列（与运营账号同一套）。监管端全程只读：
        它看不到任何金额字段，也写不了任何业务数据 —— 那两条由服务端的全局守卫在鉴权层挡着，不靠页面。
      </p>
      <n-space justify="end" class="modal-actions">
        <n-button @click="createOpen = false">取消</n-button>
        <n-button type="primary" :loading="busy" @click="create">开户</n-button>
      </n-space>
    </n-modal>
  </div>
</template>

<style scoped>
/* 页头与各块之间的纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.modal-note {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.modal-actions {
  margin-top: 20px;
}

/* 表格单元格与浮层由 naive-ui 渲染,拿不到本组件的 scope 属性 —— 用 :deep 穿进去 */
:deep(.account-name) {
  font-family: var(--sd-font-mono);
  font-size: 12.5px;
  color: var(--sd-text1);
}

:deep(.mono-time) {
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text3);
}

:deep(.dash) {
  color: var(--sd-text3);
}

:deep(.confirm) {
  max-width: 260px;
}

:deep(.confirm-text) {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 10px;
}

@media (max-width: 900px) {
  .stats {
    grid-template-columns: 1fr;
  }
}
</style>
